<template>
  <section class="docs-panel">
    <div class="docs-panel__head">
      <div>
        <h2 class="docs-panel__title">Cartella documentale</h2>
        <p class="docs-panel__subtitle">PDF caricati per questo ospite</p>
      </div>
      <button class="villa-btn villa-btn--primary" @click="openUpload">
        <i class="fa-solid fa-file-arrow-up"></i> Carica documento
      </button>
    </div>

    <div v-if="loading" class="docs-state">Caricamento documenti…</div>
    <div v-else-if="error" class="docs-state">{{ error }}</div>
    <div v-else-if="documents.length === 0" class="docs-state">
      Nessun documento. Carica il primo PDF.
    </div>

    <div v-else class="docs-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Titolo</th>
            <th>Categoria</th>
            <th>File</th>
            <th>Caricato il</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="doc in documents" :key="doc.id">
            <td>{{ doc.title }}</td>
            <td>
              <span class="doc-cat">{{ categoryLabel(doc.category) }}</span>
            </td>
            <td class="doc-file">{{ doc.originalFilename }}</td>
            <td>{{ formatDate(doc.createdAt) }}</td>
            <td class="actions-cell">
              <button
                class="icon-btn"
                title="Stampa"
                :disabled="printingId === doc.id"
                @click="print(doc)"
              >
                <i
                  :class="
                    printingId === doc.id
                      ? 'fa-solid fa-spinner fa-spin'
                      : 'fa-solid fa-print'
                  "
                ></i>
              </button>
              <button
                class="icon-btn"
                title="Apri / Scarica"
                :disabled="downloadingId === doc.id"
                @click="download(doc)"
              >
                <i class="fa-solid fa-download"></i>
              </button>
              <button
                class="icon-btn icon-btn--danger"
                title="Archivia"
                @click="askArchive(doc)"
              >
                <i class="fa-solid fa-box-archive"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modale upload -->
    <VueFinalModal v-model="uploadModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">Carica documento</h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Titolo</label>
          <input
            v-model.trim="uploadModal.title"
            type="text"
            placeholder="Opzionale: se vuoto si usa il nome del file"
          />
        </div>
        <div class="form-field form-field--full">
          <label>Categoria</label>
          <select v-model="uploadModal.category">
            <option v-for="c in categories" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </select>
        </div>
        <div class="form-field form-field--full">
          <label>File PDF <span class="required">*</span></label>
          <input
            ref="fileInput"
            type="file"
            accept="application/pdf,.pdf"
            @change="onFileChange"
          />
          <span v-if="uploadModal.fileName" class="doc-file">
            {{ uploadModal.fileName }}
          </span>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="uploadModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="uploadModal.busy"
          @click="submitUpload"
        >
          {{ uploadModal.busy ? "Caricamento…" : "Carica" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma archiviazione -->
    <VueFinalModal v-model="archiveConfirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Archivia documento</h3>
      <p class="villa-confirm__text">
        Vuoi archiviare <strong>{{ archiveConfirm.title }}</strong
        >? Non sarà più visibile in elenco.
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="archiveConfirm.busy"
          @click="archiveConfirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="archiveConfirm.busy"
          @click="confirmArchive"
        >
          {{ archiveConfirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";
import {
  printPdfFromResponse,
  isPopupBlocked,
  POPUP_BLOCKED_MESSAGE,
} from "@/utils/printPdf";

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const CATEGORIES = [
  { value: "contract", label: "Contratto" },
  { value: "identity_document", label: "Documento identità" },
  { value: "health_card", label: "Tessera sanitaria" },
  { value: "consent", label: "Consenso" },
  { value: "health_document", label: "Documento sanitario" },
  { value: "therapy_pdf", label: "Terapia" },
  { value: "menu_pdf", label: "Menù" },
  { value: "proforma", label: "Proforma" },
  { value: "billing_document", label: "Documento retta" },
  { value: "other", label: "Altro" },
];

export default {
  name: "GuestDocumentsPanel",
  props: {
    guestId: { type: [String, Number], required: true },
    // Cambiando questo valore (es. dopo "Esporta PDF") il pannello ricarica la
    // lista SENZA rimontarsi, così non distrugge le modali (vue-final-modal).
    refreshKey: { type: [Number, String], default: 0 },
  },
  watch: {
    refreshKey() {
      this.load();
    },
  },
  data() {
    return {
      documents: [],
      loading: true,
      error: "",
      downloadingId: null,
      printingId: null,
      categories: CATEGORIES,
      uploadModal: {
        open: false,
        busy: false,
        title: "",
        category: "other",
        file: null,
        fileName: "",
      },
      archiveConfirm: { open: false, busy: false, id: null, title: "" },
    };
  },
  methods: {
    ...mapActions([
      "fetchGuestDocuments",
      "uploadGuestDocument",
      "downloadGuestDocument",
      "archiveGuestDocument",
    ]),

    categoryLabel(value) {
      const found = CATEGORIES.find((c) => c.value === value);
      return found ? found.label : value;
    },
    formatDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "—";
      const day = String(dt.getDate()).padStart(2, "0");
      const month = String(dt.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${dt.getFullYear()}`;
    },

    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.documents = await this.fetchGuestDocuments(this.guestId);
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Errore nel caricamento dei documenti.";
      } finally {
        this.loading = false;
      }
    },

    // --- Upload ---
    openUpload() {
      this.uploadModal = {
        open: true,
        busy: false,
        title: "",
        category: "other",
        file: null,
        fileName: "",
      };
      // Reset dell'input file nativo (persiste nel DOM della modale).
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) {
        this.uploadModal.file = null;
        this.uploadModal.fileName = "";
        return;
      }
      this.uploadModal.file = file;
      this.uploadModal.fileName = file.name;
    },
    validateFile(file) {
      if (!file) return "Seleziona un file PDF.";
      const isPdf =
        file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
      if (!isPdf) return "Sono ammessi solo file PDF.";
      if (file.size > MAX_SIZE) return "Il file supera il limite di 10MB.";
      return "";
    },
    async submitUpload() {
      const file = this.uploadModal.file;
      const validationError = this.validateFile(file);
      if (validationError) {
        return this.$toast.error(validationError);
      }
      this.uploadModal.busy = true;
      try {
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("category", this.uploadModal.category || "other");
        if (this.uploadModal.title) {
          formData.append("title", this.uploadModal.title);
        }
        await this.uploadGuestDocument({
          guestId: this.guestId,
          formData,
        });
        this.$toast.success("Documento caricato.");
        this.uploadModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Caricamento non riuscito."
        );
      } finally {
        this.uploadModal.busy = false;
      }
    },

    // --- Stampa --- (riusa l'endpoint di download; nessuna rigenerazione)
    async print(doc) {
      this.printingId = doc.id;
      try {
        const response = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: doc.id,
        });
        printPdfFromResponse(response);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Stampa non riuscita."
        );
      } finally {
        this.printingId = null;
      }
    },

    // --- Download ---
    async download(doc) {
      this.downloadingId = doc.id;
      let url = null;
      try {
        const response = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: doc.id,
        });
        const blob = new Blob([response.data], {
          type: response.data?.type || "application/pdf",
        });
        url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          doc.originalFilename ||
          doc.storedFilename ||
          `documento-${doc.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Download non riuscito."
        );
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        this.downloadingId = null;
      }
    },

    // --- Archive (soft delete) ---
    askArchive(doc) {
      this.archiveConfirm = {
        open: true,
        busy: false,
        id: doc.id,
        title: doc.title,
      };
    },
    async confirmArchive() {
      this.archiveConfirm.busy = true;
      try {
        await this.archiveGuestDocument({
          guestId: this.guestId,
          documentId: this.archiveConfirm.id,
        });
        this.$toast.success("Documento archiviato.");
        this.archiveConfirm.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Archiviazione non riuscita."
        );
      } finally {
        this.archiveConfirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/GuestDocumentsPanel.css" />
